import { Box, Checkbox, IconButton, Modal, Paper, Typography, useTheme } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloseIcon from "@material-ui/icons/Close";
import LayersIcon from "@material-ui/icons/Layers";
import { httpClient } from "api/httpClient";
import { Datagrid } from "components/CustomizableDataGrid";
import { ExportServerPDFButton } from "components/dialogs/ExportDialog/ExportServerButton";
import { CopyableWrapper } from "components/fields/CopyableWrapper";
import { DateTimeField } from "components/fields/DateFormatField";
import { EqualField } from "components/fields/EqualField";
import { OrderReferenceField } from "components/fields/OrderReferenceField";
import { SelectEnumField } from "components/fields/SelectEnumField";
import { SsccFieldWithBarcode } from "components/fields/SsccFieldWithBarcode";
import { TargetOrBrokenPalletIconField } from "components/fields/TargetOrBrokenPalletIconField";
import { PalletsSummary } from "components/PalletsSummary";
import { DeleteButton } from "components/toolbars/DeleteButton";
import { envSettings } from "core/EnvSettings";
import { OrderStatus } from "entities/OrderStatus";
import { OrderStatusEditOrCorrectPallet, OrderStatusEditPallet } from "entities/OrderStatus";
import { PalletType } from "entities/PalletType";
import React from "react";
import {
	FunctionField,
	NumberField,
	TextField,
	useDataProvider,
	useListContext,
	useTranslate,
} from "react-admin";
import { useLocation } from "react-router-dom";
import { isNullUndEmpty } from "utils/general";
import { useAsyncColumnsStorage } from "utils/hooks/useAsyncColumnsStorage";

import { ForkliftIcon } from "../../../../components/icons/ForkliftIcon";
import { PalletIconField } from "../../../../components/icons/PalletIconField";
import { BoxesButton } from "../components/BoxesButton";
import {
	preArrivalPalletColor,
	reservedForAnotherOrder,
} from "../PickingList/components/Constants";
import { isScannerOperationMode } from "../PickingList/components/OperationModeToggleFilter";
import { AllocationResultDialog } from "./components/AllocationResultDialog";
import { BoxDetails } from "./components/BoxDetails";
import { EditPalletInfoDialog } from "./components/EditPalletInfoDialog";
import { PalletBoxMovementsDetails } from "./components/PalletBoxMovementsDetails";
import { PalletDetails } from "./components/PalletDetails";
import { PalletHistoryButton } from "./components/PalletHistory/PalletHistoryButton";
import { PalletAllocateButton, PalletStoreButton } from "./components/PalletStorageButtons";
import { CopyableStorageLinkChip } from "./components/StorageLinkChip";
import { useOrderServiceType } from "./hooks/useOrderServiceType";
import { palletsUseStyles } from "./Styles";

const palletsRowStyle =
	(orderStatuses, orderId, isCSOrIncomingPlukk, scanModifiedPalletIds) => (record) => {
		const plukkCompletedColor = orderStatuses?.[OrderStatus.PLUKK_COMPLETED]?.color || "inherit";
		const loadingCompletedColor =
			orderStatuses?.[OrderStatus.LOADING_COMPLETED]?.color || "inherit";

		const isPalletReserved = isCSOrIncomingPlukk && !!record.reserved_order_id;

		const isPreArrivalPallet = !record.order_id && record.reserved_order_id == orderId;

		let rowBackground = "inherit";
		if (isPreArrivalPallet) {
			rowBackground = preArrivalPalletColor;
		} else if (isPalletReserved) {
			rowBackground = reservedForAnotherOrder;
		} else if (record.loaded_into_truck) {
			rowBackground = loadingCompletedColor;
		} else if (record.into_preparation_place) {
			rowBackground = plukkCompletedColor;
		} else if (record.reserved_order_id == orderId) {
			rowBackground = "#23f5501a";
		}

		const style = {
			height: "50px",
			backgroundColor: rowBackground,
		};

		if (scanModifiedPalletIds.includes(record.id)) {
			style.border = "2px solid rgba(76, 175, 80, 0.5)";
			style.backgroundColor = "rgba(76, 175, 80, 0.1)";
		}

		return style;
	};

export const PalletsGrid = ({
	order,
	terminalId,
	operationMode,
	isLoadingMode,
	isMoveToPreparationMode,
	selectedPalletsForLoading,
	setSelectedPalletsForLoading,
	setBarcodeValue,
	setBarcodeDialogOpen,
	setBarcodeTitle,
	allocationResult,
	allocationResultOpen,
	setAllocationResultOpen,
	setAllocationResult,
	summaryTotals,
	scanModifiedPalletIds,
	storagePlaces,
	setStoragePlaces,
	allowStorePallet,
}) => {
	// const classes = useStyles();
	const classes = palletsUseStyles();
	const theme = useTheme();

	const { data, ids, refetch } = useListContext();
	const dataProvider = useDataProvider();
	const translate = useTranslate();
	const location = useLocation();
	const openedPalletIdRef = React.useRef(null);

	const [orderStatuses, setOrderStatuses] = React.useState({});
	const [records, setRecords] = React.useState([]);

	// Modal states
	const [selectedPallet, setSelectedPallet] = React.useState(null);
	const [modalOpen, setModalOpen] = React.useState(false);
	const [selectedBox, setSelectedBox] = React.useState(null);

	const columnsAsyncStorage = useAsyncColumnsStorage("pallets-grid-columns-storage");

	const { isPlukk, isCrossDock, isCSOrIncomingPlukk } = useOrderServiceType(order);

	const isScannerMode = isScannerOperationMode(operationMode);
	const isPlukkScannerMode = isPlukk && isScannerMode;
	const isSelectionMode = isLoadingMode || isMoveToPreparationMode;
	const isAllowEditPalletInfo =
		(isCSOrIncomingPlukk || isCrossDock) && OrderStatusEditOrCorrectPallet.includes(order.status);

	// Fetch order statuses
	// TODO - Use no-range filter, no pagination, also update the resource on the backend to handle no-range
	React.useEffect(() => {
		dataProvider
			.getList("order-statuses", {
				pagination: { page: 1, perPage: 1000 },
				sort: { field: "id", order: "ASC" },
				filter: {},
			})
			.then(({ data }) => {
				const statusesMap = {};
				data.forEach((status) => {
					statusesMap[status.id] = status;
				});
				setOrderStatuses(statusesMap);
			})
			.catch(() => {
				setOrderStatuses({});
			});
	}, [dataProvider]);

	// Update records from list context
	React.useEffect(() => {
		if (!data || !ids?.length) {
			setRecords([]);
			return;
		}
		setRecords(ids.map((id) => data[id]));
	}, [data, ids]);

	// Fetch storage places
	React.useEffect(() => {
		if (!envSettings.REACT_APP_FF_STORAGE_MODULE || !records?.length) {
			return;
		}

		const uniqueStoragePlaceIds = [
			...new Set([
				...records.map((p) => p.storage_place_id).filter((id) => !isNullUndEmpty(id)),
				...records.flatMap((p) =>
					(p.box_movements || [])
						.map((bm) => bm.storage_place_id)
						.filter((id) => !isNullUndEmpty(id))
				),
			]),
		];

		if (uniqueStoragePlaceIds.length === 0) {
			setStoragePlaces({});
			return;
		}

		const storagePlaceFilter = JSON.stringify({
			id: uniqueStoragePlaceIds,
			no_range: true,
		});
		const storagePlaceUrl = `/storage-places-lookup?filter=${encodeURIComponent(
			storagePlaceFilter
		)}`;

		httpClient(storagePlaceUrl)
			.then(({ json: storagePlacesData }) => {
				const storagePlacesMap = {};
				storagePlacesData.forEach((sp) => {
					storagePlacesMap[sp.id] = sp;
				});
				setStoragePlaces(storagePlacesMap);
			})
			.catch((err) => {
				console.error("Failed to load storage places:", err);
				setStoragePlaces({});
			});
	}, [records]);

	React.useEffect(() => {
		const params = new URLSearchParams(location.search);
		const palletIdFromUrl = params.get("palletId");

		if (palletIdFromUrl && Object.keys(data).length > 0) {
			const palletIdNum = parseInt(palletIdFromUrl, 10);

			if (openedPalletIdRef.current !== palletIdNum) {
				const palletToOpen = data[palletIdNum];
				if (palletToOpen) {
					setSelectedPallet(palletToOpen);
					setSelectedBox(null);
					setModalOpen(true);
					openedPalletIdRef.current = palletIdNum;
				}
			}
		}
	}, [location.search, data]);

	const handleRowDoubleClick = (pallet) => {
		setSelectedPallet(pallet);
		setSelectedBox(null);
		setModalOpen(true);
	};

	const handleBoxClick = (box) => {
		setSelectedBox(box);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setSelectedPallet(null);
		setSelectedBox(null);
	};

	const handlePalletLoadingSelection = (palletId) => {
		setSelectedPalletsForLoading((prev) => {
			if (prev.includes(palletId)) {
				return prev.filter((id) => id !== palletId);
			} else {
				return [...prev, palletId];
			}
		});
	};

	const handleSinglePalletAllocation = (allocationResult) => {
		// Show detailed allocation result dialog
		setAllocationResult(allocationResult);
		setAllocationResultOpen(true);
		refetch();
	};

	const handleCloseAllocationResult = () => {
		setAllocationResultOpen(false);
		setAllocationResult(null);
	};

	const gridSortable = envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE || !isScannerMode;

	return (
		<>
			{/* Pallets Summary */}
			{summaryTotals && <PalletsSummary summaryTotals={summaryTotals} />}

			{/* Grid */}
			<Box sx={{ overflow: "auto", maxWidth: "100%", maxHeight: "65vh" }}>
				<Datagrid
					className={classes.centeredDatagrid}
					data={data}
					ids={ids}
					isRowSelectable={false}
					rowDoubleClick={(id) => {
						handleRowDoubleClick(data[id]);
					}}
					bulkActionButtons={false}
					storage={columnsAsyncStorage}
					expand={
						<PalletBoxMovementsDetails
							translate={translate}
							terminalId={terminalId}
							storagePlaces={storagePlaces}
						/>
					}
					isRowExpandable={(record) => record?.box_movements?.length > 0}
					rowStyle={palletsRowStyle(
						orderStatuses,
						order.id,
						isCSOrIncomingPlukk,
						scanModifiedPalletIds
					)}
					reorderingSupport={true}
					disableColumnSettings={
						!envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE && isScannerMode
					}
				>
					{/* Selection checkbox for loading mode */}
					{isSelectionMode && (
						<FunctionField
							label=""
							render={(record) => (
								<Checkbox
									checked={selectedPalletsForLoading.includes(record.id)}
									onChange={() => handlePalletLoadingSelection(record.id)}
									disabled={
										(!envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE &&
											isPlukkScannerMode) ||
										record.loaded_into_truck ||
										(!record.is_storage_place_confirmed && (isLoadingMode || !record.is_target)) ||
										(isMoveToPreparationMode && record.into_preparation_place)
									}
									onClick={(e) => e.stopPropagation()}
								/>
							)}
						/>
					)}

					{/* Label button for CS and incoming plukk */}
					{isCSOrIncomingPlukk && (
						<FunctionField
							label=""
							render={(record) => (
								<ExportServerPDFButton
									endpoint={`pallets/${record.id}/label`}
									label={translate("osfc.buttons.label")}
								/>
							)}
						/>
					)}

					{isCSOrIncomingPlukk && !isScannerMode && (
						<FunctionField
							label=""
							render={(record) => {
								const isDeleteDisabled =
									!OrderStatusEditPallet.includes(order.status) ||
									record.is_storage_place_confirmed ||
									!!record.reserved_order_id;
								return (
									<DeleteButton
										resource="pallets"
										notifyMessage="osfc.dialogs.pallets.delete.success"
										title="osfc.dialogs.pallets.delete.title"
										content="osfc.dialogs.pallets.delete.confirm_message"
										disableDelete={isDeleteDisabled}
									/>
								);
							}}
						/>
					)}

					{isAllowEditPalletInfo &&
						(envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE || !isScannerMode) && (
							<EditPalletInfoDialog order={order} />
						)}
					<PalletIconField
						icon={ForkliftIcon}
						tooltipKey="tab.pallets.icons.high_level_pallet"
						iconStyle={{ color: "red" }}
						isVisible={(record) => !!record?.is_high_level}
					/>
					<PalletIconField
						icon={LayersIcon}
						tooltipKey="tab.pallets.icons.stacked_pallet"
						iconStyle={{ color: theme.palette.primary.main }}
						isVisible={(record) => !!record?.stack_group_id}
					/>
					<TargetOrBrokenPalletIconField />
					<FunctionField
						label=""
						render={(record) => (
							<Box onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
								<BoxesButton
									record={record}
									setBarcodeValue={setBarcodeValue}
									setBarcodeTitle={setBarcodeTitle}
									setBarcodeDialogOpen={setBarcodeDialogOpen}
								/>
							</Box>
						)}
					/>
					<FunctionField
						label=""
						render={(record) => (
							<Box onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
								<PalletHistoryButton pallet={record} />
							</Box>
						)}
					/>
					<CopyableWrapper
						source="id"
						label={translate("tab.pallets.pallet_id")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>
					{isPlukk && (
						<OrderReferenceField source="order_id" label={"tab.pallets.incoming_order"} />
					)}
					<CopyableWrapper
						source="sscc"
						label={translate("tab.pallets.sscc")}
						sortable={gridSortable}
					>
						<SsccFieldWithBarcode
							setBarcodeValue={setBarcodeValue}
							setBarcodeTitle={setBarcodeTitle}
							setBarcodeDialogOpen={setBarcodeDialogOpen}
						/>
					</CopyableWrapper>
					<CopyableWrapper
						source="sscc_original"
						label={translate("tab.pallets.sscc_original")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="gtin"
						label={translate("tab.pallets.gtin")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="production_datetime"
						label={translate("tab.pallets.production_datetime")}
						sortable={gridSortable}
					>
						<DateTimeField withLocalTimeZone />
					</CopyableWrapper>

					<CopyableWrapper
						source="expiration_datetime"
						label={translate("tab.pallets.expiration_datetime")}
						sortable={gridSortable}
					>
						<DateTimeField withLocalTimeZone />
					</CopyableWrapper>

					<NumberField
						source="quantity"
						label={translate("tab.pallets.quantity")}
						render={(record) => {
							return (
								<Box
									display="flex"
									alignItems="center"
									justifyContent="center"
									onClick={(e) => e.stopPropagation()}
								>
									<span>{record.quantity}</span>
								</Box>
							);
						}}
						sortable={gridSortable}
					/>

					<NumberField
						source="net_weight"
						label={translate("tab.pallets.weight_net")}
						render={(record) => {
							return (
								<Box
									display="flex"
									alignItems="center"
									justifyContent="center"
									onClick={(e) => e.stopPropagation()}
								>
									<span>{record.net_weight}</span>
								</Box>
							);
						}}
						sortable={gridSortable}
					/>

					<SelectEnumField
						source="type"
						enumName="pallet_type"
						enumObj={PalletType}
						label={translate("tab.pallets.type")}
						sortable={gridSortable}
					/>

					<EqualField
						source="loaded_into_truck"
						to={true}
						label={translate("tab.pallets.loaded_into_truck")}
						sortable={gridSortable}
					/>

					<FunctionField
						label={translate(
							`tab.pallets.${
								envSettings.REACT_APP_FF_STORAGE_MODULE ? "storage_place_id" : "area_id"
							}`
						)}
						render={(record) => (
							<Box display="flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
								<CopyableStorageLinkChip
									pallet={record}
									terminalId={terminalId}
									storagePlaces={storagePlaces}
									setBarcodeTitle={setBarcodeTitle}
									setBarcodeValue={setBarcodeValue}
									setBarcodeDialogOpen={setBarcodeDialogOpen}
								/>
								{(envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE || !isScannerMode) &&
									(isCSOrIncomingPlukk || isCrossDock) &&
									allowStorePallet(record) && (
										<>
											<PalletAllocateButton
												pallet={record}
												order={order}
												onSuccess={handleSinglePalletAllocation}
											/>
											<PalletStoreButton
												pallet={record}
												order={order}
												onSuccess={() => refetch()}
											/>
										</>
									)}
							</Box>
						)}
					/>

					<TextField
						source="weight_unit_code"
						label={translate("tab.pallets.weight_unit_code")}
						sortable={gridSortable}
					/>

					<NumberField
						source="number_of_pallet"
						label={translate("tab.pallets.number_of_pallet")}
						sortable={gridSortable}
					/>

					<NumberField
						source="total_number_of_pallet"
						label={translate("tab.pallets.total_pallets")}
						sortable={gridSortable}
					/>

					<CopyableWrapper
						source="ext_id"
						label={translate("tab.pallets.external_id")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="product_number"
						label={translate("tab.pallets.product_number")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="product_name"
						label={translate("tab.pallets.product_name")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="ceel_number"
						label={translate("tab.pallets.ceel_number")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>
					<CopyableWrapper
						source="batch_number"
						label={translate("tab.pallets.batch_number")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="lot_number"
						label={translate("tab.pallets.lot_number")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="packing_station"
						label={translate("tab.pallets.packing_station")}
						sortable={gridSortable}
					>
						<TextField />
					</CopyableWrapper>

					<FunctionField
						source="is_t1"
						label={translate("tab.pallets.is_t1")}
						render={(record) => (record.is_t1 ? "YES" : "NO")}
						sortable={gridSortable}
					/>
				</Datagrid>
			</Box>

			{/* Pallet Details Modal */}
			<Modal open={modalOpen} onClose={handleCloseModal} className={classes.modal}>
				<Paper className={classes.modalContent}>
					<div className={classes.modalHeader}>
						{selectedBox && (
							<IconButton onClick={() => setSelectedBox(null)}>
								<ArrowBackIcon />
							</IconButton>
						)}
						<Typography variant="h5">{selectedBox ? "Box Details" : "Pallet Details"}</Typography>
						<IconButton onClick={handleCloseModal}>
							<CloseIcon />
						</IconButton>
					</div>

					{selectedBox ? (
						<BoxDetails
							translate={translate}
							classes={classes}
							selectedBox={selectedBox}
							selectedPallet={selectedPallet}
						/>
					) : (
						<PalletDetails
							translate={translate}
							classes={classes}
							selectedPallet={selectedPallet}
							handleBoxClick={handleBoxClick}
							selectedBox={selectedBox}
							order={order}
						/>
					)}
				</Paper>
			</Modal>

			{/* Allocation Result Dialog */}
			<AllocationResultDialog
				open={allocationResultOpen}
				onClose={handleCloseAllocationResult}
				result={allocationResult}
				translate={translate}
			/>
		</>
	);
};
